import React, { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router";
import { DebounceInput } from "react-debounce-input";

import {
  useSpinner,
  Spinner,
  DataTable,
  ExportButton,
  compactNumberFormat,
} from "../../../../../shared";
import { setItemToStorage } from "../../../../../system";
import { getConversionRateTableColumns } from "../utility";
import {
  loginConstants,
  performanceConstants,
  reportTypesConstants,
} from "../../../constants";
import SankeyChart from "../../../../../shared/components/chart/sankeyChart";

const {
  ALL_BRAND_CONVERSION_RESPONSE_DATA,
  SURVEY_CONVERSION_RATE_KPI_SUMMARY_DATA,
  GOOGLE_REVIEW,
} = performanceConstants;

const ConversionRateTab = (props) => {
  const {
    getData,
    sorting,
    exportFunc,
    setSorting,
    createDateTo,
    createDateFrom,
    selectedFilter,
    brandConversionData,
    searchStringConversionRate,
    setSearchStringConversionRate,
    brandConversionDataPagination,
    surveyConversionRateKpiSummary,
  } = props;

  const {
    totalSurveySentCount,
    totalSurveyOpenedCount,
    totalGoogleReviewCount,
    totalSurveyStartedCount,
    totalSurveyCompleteCount,
    totalPassiveSurveyCompleteCount,
    totalPositiveSurveyCompleteCount,
    totalNegativeSurveyCompleteCount,
  } = surveyConversionRateKpiSummary;

  // Show loading while a request is running.
  const spinner = useSpinner();

  const isGoogleReviewFilterSelected = () =>
    selectedFilter?.value === GOOGLE_REVIEW;

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const { brandId, name } = row.original;
      return (
        <Link
          className="table-links"
          to={`/stores/brand-profile/${brandId}`}
          onClick={() => {
            setItemToStorage(loginConstants.BRAND_NAME, name);
            setItemToStorage(loginConstants.BRAND_ID, brandId);
          }}
        >
          {name}
        </Link>
      );
    },
    // eslint-disable-next-line
    []
  );

  const renderItemIfValueExist = (data) => data?.value > 0;

  useEffect(() => {
    getData(0, null, brandConversionDataPagination.size, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDateFrom, createDateTo]);

  const initialRender = useRef(true);
  useEffect(() => {
    //Stop useEffect default execution on component render
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    getData(0, null, brandConversionDataPagination.size, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStringConversionRate]);

  return (
    <>
      {(spinner(ALL_BRAND_CONVERSION_RESPONSE_DATA) ||
        spinner(SURVEY_CONVERSION_RATE_KPI_SUMMARY_DATA)) && <Spinner />}
      <div className="container-fluid pt-2">
        <div className="notification-form card shadow mb-2">
          <div className="card-body px-0 position-relative">
            <div className="position-absolute top-0 start-0 border shadow-sm rounded-2 my-2 ms-4 p-2 text-center">
              <h6 className="mb-0 fw-medium">Total Surveys Sent</h6>
              <h3 className="fw-bolder mb-0">
                {compactNumberFormat(totalSurveySentCount.value)}
              </h3>
            </div>
            <SankeyChart
              renderItemIfValueExist={renderItemIfValueExist}
              surveyConversionRateKpiSummary={surveyConversionRateKpiSummary}
              isGoogleReviewFilterSelected={isGoogleReviewFilterSelected()}
              reportType={reportTypesConstants.CONVERSION_REPORT}
            />
            <div className="sankey-chart-labels">
              {totalSurveyOpenedCount?.value > 0 && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">Survey Opened</h6>
                </div>
              )}
              {renderItemIfValueExist(totalSurveyStartedCount) && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">Surveys Started</h6>
                </div>
              )}
              {renderItemIfValueExist(totalSurveyCompleteCount) && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">Surveys Completed</h6>
                </div>
              )}
              {(renderItemIfValueExist(totalPositiveSurveyCompleteCount) ||
                renderItemIfValueExist(totalPassiveSurveyCompleteCount) ||
                renderItemIfValueExist(totalNegativeSurveyCompleteCount)) && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">Survey Completion Type</h6>
                </div>
              )}
              {isGoogleReviewFilterSelected() &&
                renderItemIfValueExist(totalGoogleReviewCount) && (
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <h6 className="fw-bold">Redirect to Google Review</h6>
                  </div>
                )}
            </div>
          </div>
        </div>
        <ExportButton
          exportFunc={exportFunc}
          isDisabled={!brandConversionDataPagination?.totalElements}
        />
        <div className="notification-form card shadow mb-5">
          <div className="card-body px-0">
            <div className="row">
              <div className="col-6 py-1">
                <h5 className="responses ps-4 m-0">Conversion Rates</h5>
              </div>
              <div className="col-6 py-1">
                <div className="search-container ms-auto me-4">
                  <DebounceInput
                    type="text"
                    value={searchStringConversionRate}
                    placeholder="Search brands"
                    debounceTimeout={1000}
                    className="search-restaurants"
                    id="search-store-locations"
                    onChange={(e) =>
                      setSearchStringConversionRate(e.target.value)
                    }
                  />
                  <i aria-hidden="true" className="fa fa-search search-icon" />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <DataTable
                columns={getConversionRateTableColumns(
                  isGoogleReviewFilterSelected,
                  cellNameRenderer
                )}
                sortedBy={sorting}
                data={brandConversionData}
                onPageChange={(pageNum) =>
                  getData(
                    pageNum,
                    null,
                    brandConversionDataPagination.size,
                    true
                  )
                }
                onColumnSort={(sortBy) => setSorting(sortBy)}
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size, true);
                }}
                pageNumber={brandConversionDataPagination.number + 1}
                pageSize={brandConversionDataPagination.size}
                totalRows={brandConversionDataPagination.totalElements}
                numberOfRowsPerPage={brandConversionDataPagination.size}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConversionRateTab;
